import type { Devo } from "~/hooks/usePlayerStore";
import { EPISODE_COMPLETION_DRAWER_THRESHOLD_PERCENTAGE } from "~/lib/constants";
import { formatScriptureReference } from "~/lib/scriptureReference";

export function getFirstMeditationTrack(devo: Pick<Devo, "meditationMoment">): string | null {
  const firstTrack = devo.meditationMoment?.tracks[0];
  return typeof firstTrack?.source === "string" && firstTrack.source.length > 0 ? firstTrack.source : null;
}

export function hasEligibleMeditationMoment(devo: Pick<Devo, "meditationMoment">): boolean {
  return Boolean(devo.meditationMoment?.hasPlayableAudio && getFirstMeditationTrack(devo));
}

export function getMeditationScriptureReferences(
  devo: Pick<Devo, "meditationMoment" | "scriptureReferences">,
): Devo["scriptureReferences"] {
  const meditationReferences = devo.meditationMoment?.scriptureReferences;
  if (Array.isArray(meditationReferences) && meditationReferences.length > 0) {
    return meditationReferences;
  }

  return devo.scriptureReferences ?? null;
}

export function getMeditationDisplayTitle(devo: Pick<Devo, "title">): string {
  return devo.title?.trim() || "Meditation";
}

export function getMeditationDisplayReference(
  devo: Pick<Devo, "meditationMoment" | "scriptureReferences">,
): string | null {
  const firstReference = getMeditationScriptureReferences(devo)?.[0];
  if (!firstReference?.book) {
    return null;
  }

  const label = formatScriptureReference({
    book: firstReference.book,
    startChapter: firstReference.startChapter ?? undefined,
    startVerse: firstReference.startVerse ?? undefined,
    endChapter: firstReference.endChapter ?? undefined,
    endVerse: firstReference.endVerse ?? undefined,
  });

  return label.length > 0 ? label : null;
}

export function shouldTriggerCompletionDrawer(args: {
  devo: Pick<Devo, "meditationMoment">;
  percentage: number;
  ended: boolean;
}): boolean {
  return (
    hasEligibleMeditationMoment(args.devo) &&
    (args.ended || args.percentage >= EPISODE_COMPLETION_DRAWER_THRESHOLD_PERCENTAGE)
  );
}
